/**
 * MarkdownRenderer — minimal in-portal Markdown → JSX renderer (NC-597).
 *
 * Deliberately small (no react-markdown dep) to keep the bundle and the npm
 * audit surface flat. Supports the subset used by the in-app wiki:
 *   - # / ## / ### headings
 *   - paragraphs (blank line separated)
 *   - unordered lists (lines starting with "- ")
 *   - inline: **bold**, *italic*, `code`, [text](url)
 *
 * Not supported: tables, ordered lists, code fences. If the wiki content
 * outgrows this, swap to react-markdown via a single import-change here.
 */

import React from 'react';

interface InlineToken {
  kind: 'text' | 'bold' | 'italic' | 'code' | 'link';
  value: string;
  href?: string;
}

function tokenizeInline(line: string): InlineToken[] {
  const out: InlineToken[] = [];
  let i = 0;
  let buf = '';
  const flush = () => {
    if (buf) {
      out.push({ kind: 'text', value: buf });
      buf = '';
    }
  };

  while (i < line.length) {
    // Inline code: `...`
    if (line[i] === '`') {
      const end = line.indexOf('`', i + 1);
      if (end !== -1) {
        flush();
        out.push({ kind: 'code', value: line.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    // Bold: **...**
    if (line[i] === '*' && line[i + 1] === '*') {
      const end = line.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        out.push({ kind: 'bold', value: line.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    // Italic: *...* (single asterisk, but not the bold delimiter)
    if (line[i] === '*' && line[i + 1] !== '*') {
      const end = line.indexOf('*', i + 1);
      if (end !== -1) {
        flush();
        out.push({ kind: 'italic', value: line.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    // Link: [text](url)
    if (line[i] === '[') {
      const closeText = line.indexOf(']', i + 1);
      if (closeText !== -1 && line[closeText + 1] === '(') {
        const closeUrl = line.indexOf(')', closeText + 2);
        if (closeUrl !== -1) {
          flush();
          out.push({
            kind: 'link',
            value: line.slice(i + 1, closeText),
            href: line.slice(closeText + 2, closeUrl),
          });
          i = closeUrl + 1;
          continue;
        }
      }
    }
    buf += line[i];
    i += 1;
  }
  flush();
  return out;
}

function renderInline(tokens: InlineToken[]): React.ReactNode[] {
  return tokens.map((t, idx) => {
    const key = `inline-${idx}`;
    switch (t.kind) {
      case 'bold':
        return <strong key={key}>{t.value}</strong>;
      case 'italic':
        return <em key={key}>{t.value}</em>;
      case 'code':
        return (
          <code
            key={key}
            style={{ background: '#f5f5f5', padding: '0.1em 0.3em', borderRadius: 3, fontSize: '0.9em' }}
          >
            {t.value}
          </code>
        );
      case 'link':
        return (
          <a key={key} href={t.href} target="_blank" rel="noopener noreferrer">
            {t.value}
          </a>
        );
      default:
        return <React.Fragment key={key}>{t.value}</React.Fragment>;
    }
  });
}

/**
 * NC-794: source is typed as optional (string | null | undefined) so a missing
 * or not-yet-loaded value never reaches the `.split()` call. Any falsy value
 * renders an empty, stable container instead of throwing. The `data-testid` is
 * always present so callers can assert the component mounted without content.
 */
export function MarkdownRenderer({ source }: { source?: string | null }) {
  if (!source) {
    return <div data-testid="markdown-rendered" />;
  }
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const joined = paragraph.join(' ');
    blocks.push(
      <p key={`p-${blocks.length}`} style={{ lineHeight: 1.6 }}>
        {renderInline(tokenizeInline(joined))}
      </p>
    );
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} style={{ paddingLeft: '1.5em' }}>
        {listItems.map((item, idx) => (
          <li key={`li-${idx}`} style={{ marginBottom: 4 }}>
            {renderInline(tokenizeInline(item))}
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h3 key={`h-${blocks.length}`} style={{ marginTop: '1.2em' }}>
          {renderInline(tokenizeInline(line.slice(4)))}
        </h3>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h2 key={`h-${blocks.length}`} style={{ marginTop: '1.4em' }}>
          {renderInline(tokenizeInline(line.slice(3)))}
        </h2>
      );
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h1 key={`h-${blocks.length}`} style={{ marginTop: '0.5em' }}>
          {renderInline(tokenizeInline(line.slice(2)))}
        </h1>
      );
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      listItems.push(line.slice(2));
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  return <div data-testid="markdown-rendered">{blocks}</div>;
}

export default MarkdownRenderer;
